import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Shield, Award, Users, Heart } from 'lucide-react';
import { companyInfo } from '../data/mock';

const About = () => {
  const values = [
    {
      icon: Shield,
      title: "Qualité et sécurité",
      description: "Nous utilisons uniquement des matériaux de qualité professionnelle et respectons toutes les normes de sécurité sur nos chantiers."
    },
    {
      icon: Award,
      title: "Expertise reconnue",
      description: "Fort de plus de 10 ans d'expérience, notre savoir-faire est reconnu dans toute la région Hauts-de-France."
    },
    {
      icon: Users,
      title: "Satisfaction client",
      description: "Votre satisfaction est notre priorité. Nous vous accompagnons à chaque étape de votre projet avec transparence."
    },
    {
      icon: Heart,
      title: "Respect de l'environnement",
      description: "Nous privilégions des solutions éco-responsables et des matériaux respectueux de l'environnement."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-cyan-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              À propos de {companyInfo.name}
            </h1>
            <p className="text-xl text-gray-600">
              {companyInfo.tagline}
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Notre histoire
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Les Façadiers HDF est une entreprise artisanale spécialisée dans le ravalement 
                de façade et l'isolation thermique par l'extérieur dans toute la région 
                Hauts-de-France. Bien que jeune, notre entreprise s'appuie sur une solide 
                expertise de plus de 10 ans acquise au sein de différentes sociétés 
                d'experts façadiers.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Cette expérience nous permet de maîtriser parfaitement les techniques de 
                ravalement, d'isolation et de rénovation de façades. Notre mission est de 
                redonner vie aux façades tout en améliorant le confort et la performance 
                énergétique des habitations. Chaque projet est unique et mérite une attention 
                particulière.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Nous mettons un point d'honneur à respecter les délais, à utiliser des 
                matériaux de qualité et à offrir un service personnalisé à chacun de nos clients. 
                Notre ancienneté dans le domaine est votre garantie d'un travail professionnel 
                et soigné.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://customer-assets.emergentagent.com/job_facade-artisan/artifacts/0zs6twu5_IMG_1429.jpeg"
                alt="Notre équipe au travail"
                className="rounded-xl h-64 w-full object-cover shadow-lg"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_facade-artisan/artifacts/yfxmmqka_IMG_1606.jpeg"
                alt="Résultat de qualité"
                className="rounded-xl h-64 w-full object-cover shadow-lg mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nos valeurs
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Ce qui nous guide au quotidien
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="border-gray-200 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6 text-center space-y-4">
                  <div className="bg-cyan-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
                    <value.icon className="w-8 h-8 text-cyan-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-600 via-cyan-500 to-teal-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-12 text-center">
            <div>
              <div className="text-5xl font-bold text-white mb-2">{companyInfo.experience}</div>
              <div className="text-xl text-cyan-50">ans d'expérience</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-white mb-2">500+</div>
              <div className="text-xl text-cyan-50">projets réalisés</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-white mb-2">100%</div>
              <div className="text-xl text-cyan-50">clients satisfaits</div>
            </div>
          </div>
        </div>
      </section>

      {/* Engagements Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nos engagements
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-gray-50 p-8 rounded-xl">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Transparence totale</h3>
              <p className="text-gray-600">
                Devis détaillé, planning précis et communication régulière tout au long du chantier.
              </p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Travail soigné</h3>
              <p className="text-gray-600">
                Chaque détail compte. Nous attachons une importance particulière à la finition de nos travaux.
              </p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Respect des délais</h3>
              <p className="text-gray-600">
                Nous nous engageons sur un planning réaliste et faisons tout pour le respecter.
              </p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Garantie qualité</h3>
              <p className="text-gray-600">
                Tous nos travaux sont garantis et nous restons disponibles après la fin du chantier.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;